import '@module/PriceCalendar/_pricecalendar.scss';
import { dateAddDays, dateFormat, dateFormatYMD, dateIsBefore, dateIsPast, dateStartOfDay, dateSubDays } from '@utils/dateFns';
import { arrayMergePrimitive, debounce, deepCopy, getNodeIndex, getUrlVarBase64, getUrlVars, isDesktopView, isEmptyObject, isMobileView, isNetworkError, l, lc, logEvent, multiDimensionalUnique, prevAll, qs, qsa, shuffleArray, sleep, urlParameterSet } from '@utils/toolbox';
import { ConnectApi } from '@js/plugins/connectApi';
import { resultsRender } from '@components/resultsRender';
import { Civet } from '@js/plugins/civet';
import { eventCategories, events } from '@js/utils/event';
import { getHolidayData } from '@js/holidays/datepicker.holidays';
import { PriceCalendarOnWay } from './PriceCalendarOneWay';
import { useResultQueryStore } from '@v/components/Filters/store/resultQueryStore';

const _defaults = {
	sortTypes: {
		price: 'cheapest',
		traveltime: 'fastest',
		bestValue: 'best',
		emission: 'emission'
	},
	sortTypesReversed: {
		cheapest: 'price',
		fastest: 'travelTime',
		best: 'bestValue',
		emission: 'emission'
	},
	config: {
		nearbyDates: 0,
		selfInstanceData: null
	},
	grid: {
		departDates: [],
		returnDates: [],
		minDays: 28,
		maxSearch: 3,
		maxScroll: 5
	}
};

export class PriceCalendar {
	constructor (html, nearbyDates, sorting, filters) {
		if (js_params.returntrip === 0) {
			return new PriceCalendarOnWay(nearbyDates);
		}
		this.nearbyDates = nearbyDates;

		this.returnTrip = js_params.returntrip;

		// When price calendar is opened searchGrid is called twice, once from the open function and once from the onScroll function.
		// This variable is used to prevent the second call from being made.

		this.data = {
			dates: js_params?.module?.PriceCalendar?.searchData,
			sorting: sorting || js_params.defaultSorting,
			filter: this.getFormattedFiltersFromURL(filters)
		};

		this.departDate = new Date(this.data.dates[0]);
		this.returnDate = this.returnTrip ? new Date(this.data.dates[1]) : null;

		this.initialised = false;
		this.minMaxPrices = null;
		this.autoSearchEnabled = (this.nearbyDates === true) || getUrlVars().forceNearbyDates;

		this.searches = {};
		this.meta = {};
		this.searchLoading = false;
		this.searchCount = 0;
		this.searchQueue = [];
		this.previouslySearchedCells = [];

		this.prevScrollCount = 0;
		this.nextScrollCount = 0;
		this.scrollTop = 0;
		this.scrollRight = 0;
		this.scrollBottom = 0;
		this.scrollLeft = 0;

		this.grid = _defaults.grid;
		this.html = html;
		this.events = {};
		this.loader = this.html.querySelector('.priceCalendar__loader');

		this.elems = {
			inner: this.html.querySelector('.priceCalendar__inner')
		};

		this.setDates();
		this.setHolidays();
		this.render();
		this.setSorting(getUrlVars()?.sorting || 'bestValue');
		this.highlightDefaultCells();
	}

	scrollToCheapest () {
		const cheapestCell = this.html.querySelector('.priceCalendar__cell--lowPrice');
		if (cheapestCell) {
			cheapestCell.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
		}
	}

	getFormattedFiltersFromURL (filters) {
		if (!filters) {
			return null;
		}

		// Decode the filters fetched from the URL
		const _filters = deepCopy(filters);
		// Strip the keys that should not be sent
		delete _filters.activeTimetableBtns;
		delete _filters.sortEmission;

		return _filters;
	}

	searchGrid (isUpdate = false, getAllCells = false, chunkSize = 1) {
		this.searchLoading = true;

		// Abort any previous chunk chain
		this.chunkAbortController?.abort?.();

		// Create a new AbortController for this chain
		const abortController = new AbortController();
		this.chunkAbortController = abortController;

		const visibleCells = this.getVisibleCellDates();

		// Determine which cells to search
		if (isUpdate || getAllCells) {
			this.data.dates = visibleCells;
			console.info('getting all cells', this.data.dates.length);
		} else {
			this.data.dates = this.filterPrevSearchedDates(visibleCells);
			console.info('only updating unsearched cells', this.data.dates.length);
		}

		// Filter dates based on returnTrip config
		this.data.dates = this.data.dates.filter(cell =>
			this.returnTrip ? !!cell[0] && !!cell[1] : !!cell[0]
		);

		if (!this.data.dates.length) {
			return Promise.resolve(); // Nothing to do
		}

		this.showLoader();

		const { dates, sorting } = this.data;

		// Split dates into chunks
		// const chunks = [];
		// for (let i = 0; i < dates.length; i += chunkSize) {
		// 	chunks.push(dates.slice(i, i + chunkSize));
		// }

		// Wrap the chain in a new Promise
		return new Promise((resolve, reject) => {
			// Use an async function to process chunks sequentially
			const processChunksSequentially = async () => {
				for (const chunk of dates) {
					// Process the current chunk
					const response = await ConnectApi(
						'PriceCalendarAPI',
						'searchGrid',
						[js_params.id, chunk, this.data.filter, sorting]
					);

					if (abortController.signal.aborted) {
						// If the signal is aborted, throw an error to break the loop
						throw new Error('Chunk chain aborted 1');
					}

					// Process response data
					this.mergeMeta(response);
					this.searches = Object.assign(this.searches, response.data);
					this.populateCells();
					this.setPrevSearchedDates();
				}
			};

			processChunksSequentially()
				.catch((error) => {
					// If we failed for any other reason than aborting, we want to reject the promise
					if (!abortController.signal.aborted) {
						reject(error);
					}
				})
				.finally(async () => {
					this.searchLoading = false;
					if (this.autoSearchEnabled) {
						await sleep(1100);
						this.doAutoSearch();
					}
					this.hideLoader();
					resolve();
				});
		});
	}

	setPrevSearchedDates () {
		Object.entries(this.searches).forEach(([key, value]) => {
			if (this.returnTrip) {
				this.previouslySearchedCells.push([value.bestValue.leaveDate, value.bestValue.homeDate]);
			} else {
				this.previouslySearchedCells.push([value.bestValue.leaveDate]);
			}
		});
		this.previouslySearchedCells = multiDimensionalUnique(this.previouslySearchedCells);
	}

	filterPrevSearchedDates (visibleDates) {
		const prevSearchedDates = this.previouslySearchedCells;

		for (let i = 0; i < visibleDates.length; i++) {
			for (let k = 0; k < prevSearchedDates.length; k++) {
				const d = visibleDates[i];
				const b = prevSearchedDates[k];

				if (d && b) {
					if (d[0] === b[0] && b[1] === d[1]) {
						visibleDates.splice(i, 1);
					}
				}
			}
		}

		return visibleDates;
	}

	getVisibleCellDates () {
		const isReturnTrip = this.returnTrip;
		const cells = qsa('.priceCalendar__cell.visible', this.html);

		const cellValues = isReturnTrip
			? cells.map(cell => [cell.dataset.departdate, cell.dataset.returndate])
			: cells.map(cell => [cell.dataset.departdate]);

		return cellValues;
	}

	setVisibleCells () {
		const isReturnTrip = this.returnTrip;
		const cells = qsa('.priceCalendar__cell:not(.priceCalendar__cell--side):not(.priceCalendar__cell--head)', this.html);
		const xOffset = isReturnTrip ? this.html.querySelector('.priceCalendar__col--left').offsetWidth : 0;
		const yOffset = this.html.querySelector('.priceCalendar__cell--head').offsetHeight;
		const gridWrap = this.html.querySelector('.priceCalendar__inner');
		const parentPos = gridWrap.getBoundingClientRect();

		const gridWidth = gridWrap.clientWidth;
		const gridHeight = gridWrap.clientHeight;

		cells.forEach((cell) => {
			const cellBounds = cell.getBoundingClientRect();
			const cellTop = cellBounds.top - parentPos.top;
			const cellLeft = cellBounds.left - parentPos.left;

			const notAboveGrid = (cellTop + (cellBounds.height / 2)) >= yOffset;
			const notBelowGrid = (cellTop + (cellBounds.height / 2)) <= gridHeight;
			const notLeftOfGrid = (cellLeft + (cellBounds.width / 2)) >= xOffset;
			const notRightOfGrid = (cellLeft + (cellBounds.width / 2)) <= gridWidth;

			cell.classList.remove('visible');

			if (notAboveGrid && notBelowGrid && notLeftOfGrid && notRightOfGrid) {
				cell.classList.add('visible');
			}
		});
	}

	setSorting (sorting) {
		this.data.sorting = sorting;

		qsa('div.priceCalendarSorting').forEach((inputs) => {
			const input = inputs.querySelector(`input[value="${this.data.sorting}"]`);

			if (input) {
				input.checked = true;
			}
		});

		if (this.initialised) {
			this.populateCells(true);
		}

		this.hideLoader();
	}

	async setFilters (filters) {
		const urlFilters = this.getFormattedFiltersFromURL(filters);

		// const isPreviouslyFiltered = filters.filter === null && this.data.filter !== null;
		const getAllCells = Object.keys(urlFilters).length === 0;
		this.searches = {};
		this.meta = {};
		this.data.filter = urlFilters;
		this.resetSearchedCells();

		if (this.initialised) {
			await this.searchGrid(true, getAllCells);
		}
	}

	setDates () {
		const departDate = this.departDate;
		const minDays = this.grid.minDays;
		let minDepartDate = dateSubDays(departDate, minDays);

		if (dateIsPast(minDepartDate)) {
			minDepartDate = dateSubDays(dateStartOfDay(new Date()), 1);
		}

		const maxDepartDate = dateAddDays(departDate, minDays);
		const isReturnTrip = this.returnTrip;

		while (minDepartDate < maxDepartDate) {
			minDepartDate = dateAddDays(minDepartDate, 1);
			this.grid.departDates.push(minDepartDate);
		}

		if (isReturnTrip) {
			const returnDate = this.returnDate;
			let minReturnDate = dateSubDays(returnDate, minDays);

			if (dateIsPast(minReturnDate)) {
				minReturnDate = dateSubDays(dateStartOfDay(new Date()), 1);
			}

			const maxReturnDate = dateAddDays(returnDate, minDays);

			while (minReturnDate < maxReturnDate) {
				minReturnDate = dateAddDays(minReturnDate, 1);
				this.grid.returnDates.push(minReturnDate);
			}
		}
	}

	setHolidays () {
		this.holidayData = getHolidayData();
	}

	render () {
		let html = '';
		const grid = this.html.querySelector('.priceCalendar__grid');
		const isReturnTrip = this.returnTrip;
		const departDates = this.grid.departDates;
		const returnDates = this.grid.returnDates;

		if (isReturnTrip) {
			const numCols = departDates.length;

			for (let i = 0; i <= departDates.length; i++) {
				if (i === 0 && isReturnTrip) {
					html += `
						<div class="priceCalendar__col priceCalendar__col--left">
							<div class="priceCalendar__cell priceCalendar__cell--head">
								<div class="priceCalendar__departTxt">
									<span>${l('global.simplePhrases.depart')}</span>
								</div>
								<div class="priceCalendar__returnTxt">
									<span>${l('global.simplePhrases.return')}</span>
								</div>
							</div>
							${returnDates.map(date => this.renderCellAxis('side', date)).join('')}
						</div>
					`;
				} else {
					html += `<div class="priceCalendar__col">`;

					for (let j = 0; j <= returnDates.length; j++) {
						if (j === 0) {
							html += this.renderCellAxis('head', departDates[i - 1]);
						} else {
							const departDate = departDates[i - 1];
							const returnDate = returnDates[j - 1];

							html += this.renderCell({ departDate, returnDate });
						}
					}

					html += `</div>`;
				}
			}
		} else {
			const numCols = 1;

			for (let i = 0; i <= departDates.length - 1; i++) {
				html += `<div class="priceCalendar__col">`;

				for (let j = 0; j <= numCols; j++) {
					if (j === 0) {
						html += this.renderCellAxis('head', departDates[i]);
					} else {
						const departDate = departDates[i];
						const returnDate = null;

						html += this.renderCell({ departDate, returnDate });
					}
				}

				html += `</div>`;
			}
		}

		grid.innerHTML = html;
	}

	renderCellUnavaliable (departureDate, returnDate) {
		if (returnDate) {
			return `<div class="priceCalendar__cell priceCalendar__cell--unavailable visible" data-departdate="${departureDate}" data-returndate="${returnDate}">${l('showresult.priceCalendar.errorNoResults')}</div>`;
		}

		return `<div class="priceCalendar__cell priceCalendar__cell--unavailable visible" data-departdate="${departureDate}">${l('showresult.priceCalendar.errorNoResults')}</div>`;
	}

	renderCellEmpty () {
		return `<div class="priceCalendar__cell priceCalendar__cell--unavailable">${l('showresult.priceCalendar.invalidDate')}</div>`;
	}

	renderCell (dates) {
		const { departDate, returnDate } = dates;

		const returnIsBeforeDeparture = dateIsBefore(returnDate, departDate);
		const isHistoricDate = dateIsBefore(departDate, dateStartOfDay(new Date()));

		if (returnIsBeforeDeparture || isHistoricDate) {
			return this.renderCellEmpty();
		}

		return `<div class="priceCalendar__cell" data-departdate="${dateFormatYMD(departDate)}" ${returnDate ? `data-returndate="${dateFormatYMD(returnDate)}"` : ``}>${l('global.simplePhrases.getPrice')}</div>`;
	}

	renderCellAxis (axis, date) {
		let cellStyle = `priceCalendar__cell priceCalendar__cell--${axis}`;

		// swedish styling - use special styles if Sunday OR a holiday (röd dag)
		let isHoliday;

		if (this.holidayData) {
			if (this.holidayData?.[date.toISOString().split('T')[0]]) {
				isHoliday = true;
			}

			if (isHoliday) {
				cellStyle += (axis === 'head') ? ` priceCalendar__cell--redHead` : ` priceCalendar__cell--redSide`;
			}
		}

		return `
			<div class="${cellStyle}" data-day="${date.toString().substring(0, 3)}">
				<div class="priceCalendar__cellDate">${dateFormat(date, 'MM-dd')}</div>
				<div class="priceCalendar__cellDate ${isHoliday ? 'priceCalendar__cellDate--holiday' : ''}">${dateFormat(date, 'EEE')}</div>
			</div>
		`;
	}

	renderCellQueued (cell) {
		const html = `
			<div class="priceCalendar__queued">
				<span>${l('priceCalendar.information.queuedText')}</span>
			</div>
		`;

		cell.innerHTML = html;
		cell.classList.add('priceCalendar__cell--queued');
	}

	renderCellSearching (cell) {
		const html = `
				<div class="priceCalendar__searching">
					<div class="priceCalendar__progress">
						<div class="priceCalendar__progressBar"></div>
					</div>
				</div>
			</div>
		`;

		cell.classList.remove('priceCalendar__cell--queued');
		cell.classList.add('priceCalendar__cell--searching');
		cell.innerHTML = html;
	}

	renderCellSearched (cell, id) {
		const searchData = this?.searches?.[id]?.[this.data.sorting];

		if (cell && searchData) {
			cell.classList.remove('priceCalendar__cell--flashed', 'priceCalendar__cell--searching', 'priceCalendar__cell--unavailable');

			cell.setAttribute('data-id', id);
			cell.setAttribute('data-price', searchData.lowestprice);
			cell.classList.add('priceCalendar__cell--searched');
			cell.innerHTML = lc(searchData.lowestprice);
		} else {
			throw new Error('Price calendar cell: no result!');
		}
	}

	setMinMaxPrices () {
		const searches = this.searches;
		const bestPricesArr = [];
		const cheapestPricesArr = [];
		const emissionPricesArr = [];
		const fastestPricesArr = [];
		const getMin = arr => Math.min(...arr);
		const getMax = arr => Math.max(...arr);

		for (const search in searches) {
			const bestPrice = searches[search].bestValue.lowestprice;
			const cheapestPrice = searches[search].price.lowestprice;
			const emissionPrice = searches[search].emission.lowestprice;
			const fastestPrice = searches[search].traveltime.lowestprice;

			bestPricesArr.push(bestPrice);
			cheapestPricesArr.push(cheapestPrice);
			emissionPricesArr.push(emissionPrice);
			fastestPricesArr.push(fastestPrice);
		}

		this.minMaxPrices = {
			price: [
				getMin(cheapestPricesArr),
				getMax(cheapestPricesArr)
			],
			traveltime: [
				getMin(fastestPricesArr),
				getMax(fastestPricesArr)
			],
			bestValue: [
				getMin(bestPricesArr),
				getMax(bestPricesArr)
			],
			emission: [
				getMin(emissionPricesArr),
				getMax(emissionPricesArr)
			]
		};

		this.renderMinMaxPrices();
	}

	renderMinMaxPrices () {
		const sortings = this.minMaxPrices;
		const searched = qsa('.priceCalendar__cell--searched', this.html);
		const minPrice = sortings[this.data.sorting][0];

		searched.forEach((cell) => {
			const price = parseInt(cell.dataset.price, 10);
			cell.classList.remove('priceCalendar__cell--lowPrice');

			if (price === minPrice) {
				cell.classList.add('priceCalendar__cell--lowPrice');
			}
		});
	}

	populateSearchQueue () {
		// clear search queue
		this.searchQueue = [];

		// populate searchQueue with cells
		let departDate = dateSubDays(this.departDate, 2);
		const isForceNearbyDates = getUrlVars().forceNearbyDates;

		if (this.returnTrip) {
			let returnDate = dateSubDays(this.returnDate, 2);

			for (let i = 0; i < 3; i++) {
				departDate = dateAddDays(departDate, 1);

				for (let j = 0; j < 3; j++) {
					returnDate = dateAddDays(returnDate, 1);
					const selector = `.priceCalendar__cell[data-departdate="${dateFormatYMD(departDate)}"][data-returndate="${dateFormatYMD(returnDate)}"]`;
					const cell = this.html.querySelector(selector);

					if (cell) {
						const cellNotSearched = !cell.classList.contains('priceCalendar__cell--searched');
						const cellNotDisabled = !cell.classList.contains('priceCalendar__cell--unavailable');
						const cellVisible = cell.classList.contains('visible');

						if (isForceNearbyDates && cell.dataset.departdate === dateFormatYMD(this.departDate) && cell.dataset.returndate === dateFormatYMD(this.returnDate)) {
							cell.outerHTML = this.renderCellUnavaliable(cell.dataset.departdate, cell.dataset.returndate);
							continue;
						}

						if (cellNotSearched && cellNotDisabled && cellVisible) {
							this.searchQueue.push({ departDate, returnDate });
						}
					}
				}

				// reset return date!
				returnDate = dateSubDays(this.returnDate, 2);
			}
		} else {
			for (let i = 0; i < 3; i++) {
				departDate = dateAddDays(departDate, 1);
				const departDateString = dateFormatYMD(departDate);
				const selector = `.priceCalendar__cell[data-departdate="${departDateString}"]`;
				const cell = this.html.querySelector(selector);

				if (cell) {
					const cellNotSearched = !cell.classList.contains('priceCalendar__cell--searched');
					const cellNotDisabled = !cell.classList.contains('priceCalendar__cell--unavailable');
					const cellVisible = cell.classList.contains('visible');

					if (isForceNearbyDates && cell.dataset.departdate === dateFormatYMD(this.departDate)) {
						cell.outerHTML = this.renderCellUnavaliable(departDateString);
						continue;
					}

					if (cellNotSearched && cellNotDisabled && cellVisible) {
						this.searchQueue.push({ departDate });
					}
				}
			}
		}

		// shuffle cells for randomness
		this.searchQueue = shuffleArray(this.searchQueue);
	}

	async getCheapeastDatesFromPriceCutterMini () {
		if (!js_params?.module?.PriceCalendar?.autoSearchCheapestDates || !window.showresult.priceCutterMini) {
			return [];
		}

		const cheapestDates = await window.showresult.priceCutterMini.getCheapestDates();

		// Filter out dates that aren't visible
		const visibleCells = this.getVisibleCellDates().map(cell => ({ departDate: cell[0], returnDate: cell?.[1] }));

		// Filter cheapest dates to only include visible cells
		const cheapestDatesFiltered = cheapestDates.filter((date) => {
			const cell = visibleCells.find(cell => cell.departDate === date.departDate && cell?.returnDate === date?.returnDate);
			return cell;
		});

		return cheapestDatesFiltered;
	}

	async doAutoSearch () {
		this.populateSearchQueue();

		const cheapestDates = await this.getCheapeastDatesFromPriceCutterMini();

		// If this.searchQueue is less than 8 then we add the cheapest dates from the price cutter mini so that we have a total of 8 searches.
		if (cheapestDates.length && this.searchQueue.length < 8) {
			this.searchQueue.push(...cheapestDates);

			// Slice the searchQueue to only include 8 searches.
			this.searchQueue = this.searchQueue.slice(0, 8);
			console.debug('Adding cheapest dates from price cutter mini to search queue', this.searchQueue);
		}

		for (const item of this.searchQueue) {
			const departDate = item.departDate;
			const returnDate = item?.returnDate;

			// Check if is return trip
			const selector = returnDate
				? `.priceCalendar__cell[data-departdate="${dateFormatYMD(departDate)}"][data-returndate="${dateFormatYMD(returnDate)}"]`
				: `.priceCalendar__cell[data-departdate="${dateFormatYMD(departDate)}"]`;

			const element = qs(selector, this.html);

			if (!element.classList.contains('priceCalendar__cell--searched')) {
				element.click();
				await sleep(10);
			}
		}

		this.autoSearchEnabled = false;
		urlParameterSet('nearbyDates', 0);
	}

	getAvailableCells () {
		const cell = '.priceCalendar__cell';
		const cellUnavailable = `${cell}--unavailable`;
		const cellHead = `${cell}--head`;
		const cellSide = `${cell}--side`;
		const selector = `${cell}:not(${cellHead}):not(${cellSide})`;

		return qsa(selector, this.html);
	}

	getSearchedCells () {
		return qsa('.priceCalendar__cell--searched', this.html);
	}

	resetSearchedCells () {
		const cells = this.getSearchedCells();

		cells.forEach((cell) => {
			cell.classList.remove(
				'priceCalendar__cell--searched',
				'priceCalendar__cell--lowPrice',
				'priceCalendar__cell--maxPrice'
			);
			cell.removeAttribute('data-id');
			cell.removeAttribute('data-price');
			cell.innerHTML = l('global.simplePhrases.getPrice');
			// cell.innerHTML = l('priceCalendar.information.loading');
		});
	}

	populateCells (onlySearchedCells = false) {
		const searches = this.searches;

		let cells;

		if (onlySearchedCells) {
			cells = this.getSearchedCells();
			this.resetSearchedCells();
		} else {
			cells = this.getAvailableCells();
		}

		const findReturnTrip = (cell, search) => cell.dataset.departdate === search.leaveDate && cell.dataset.returndate === search.homeDate;
		const findOnewayTrip = (cell, search) => cell.dataset.departdate === search.leaveDate;
		const findTrip = this.returnTrip ? findReturnTrip : findOnewayTrip;

		// Loop over all searches and update cells with price
		for (const id in searches) {
			const search = searches[id][this.data.sorting];
			const cellIndex = cells.findIndex(cell => findTrip(cell, search));
			this.renderCellSearched(cells[cellIndex], id);
			cells.splice(cellIndex, 1);
		}

		if (this.initialised) {
			this.setMinMaxPrices();
		}
	}

	async search (cell) {
		// We first call Search->initiateSearch, followed by Search->update and then PriceCalendarAPI->getPrice.
		// PriceCalendarAPI should be updated so that we only send 1 request!!

		const departDate = cell.dataset.departdate;
		const returnDate = cell.dataset.returndate;

		logEvent(events.calendarsearch, eventCategories.search, { departure: departDate, return: returnDate ? returnDate : false });

		const cellProgressBar = cell.querySelector('.priceCalendar__progressBar');
		const interval = 200;

		let percent = 0;
		let updateInterval = null;

		let fakeInterval = setInterval(() => {
			if (percent < 100) {
				updateCellProgress();
			}
		}, interval);

		const finishSearch = () => {
			fakeInterval = setInterval(() => {
				if (percent < 100) {
					updateCellProgress();
				} else {
					clearInterval(fakeInterval);
					this.highlightDefaultCells();
				}
			}, interval);
		};

		const updateCellProgress = (error = false) => {
			if (error) {
				percent = 100;

				cell.innerHTML = `
					<div class="priceCalendar__error">
						<span>${l('showresult.priceCalendar.errorNoResults')}</span>
					</div>
				`;

				return;
			}

			percent++;

			const progress = percent + '%';

			cellProgressBar.style.setProperty('--progress', progress);
		};

		const initSearch = () => {
			return new Promise((resolve, reject) => {
				const dateArray = [departDate];

				if (returnDate) {
					dateArray.push(returnDate);
				}

				ConnectApi('Search', 'mimicSearch', [js_params.id, dateArray]).then((reference) => {
					if (!reference.hasOwnProperty('results') || !reference.results.hasOwnProperty('server')) {
						reject(reference);
					}

					resolve(reference);
				}).catch(reject);
			});
		};

		const updateSearch = (reference) => {
			return new Promise((resolve, reject) => {
				updateInterval = setInterval(() => {
					ConnectApi('Search', 'update', [{ responseId: reference.results.id }], true).then((data) => {
						if (data.results !== undefined) {
							if (data.results.status === 'complete') {
								resolve(reference.results.id);
								console.log('Search complete', reference.results.id);
							}
						} else if (data.error !== undefined) {
							const error = new Error(data.error);
							error.error_code = data.error_code;
							reject(error);
						}
					}).catch(reject);
				}, 2000);
			});
		};

		const getPrice = (response) => {
			const data = [response];

			if (!isEmptyObject(this.data.filter)) {
				const filters = getUrlVarBase64('filter');
				data.push(filters);
			}

			return ConnectApi('PriceCalendarAPI', 'getPrice', data, true);
		};

		const handleResponse = (response) => {
			clearInterval(updateInterval);

			const responseId = Object.keys(response.data)[0];

			this.mergeMeta(response);
			this.searches = Object.assign(this.searches, response.data);

			if (this.returnTrip) {
				this.previouslySearchedCells.push([response.leaveDate, response.homeDate]);
			} else {
				this.previouslySearchedCells.push([response.leaveDate]);
			}

			this.renderCellSearched(cell, parseInt(responseId));
			this.setMinMaxPrices();

			this.searchCount--;

			finishSearch();
		};

		try {
			const reference = await initSearch();
			const searchStatus = await updateSearch(reference);
			const response = await getPrice(searchStatus);
			await handleResponse(response);
		} catch (error) {
			// console.trace(error);
			clearInterval(updateInterval);
			clearInterval(fakeInterval);
			this.searchCount--;

			const _isNetworkError = isNetworkError(error);

			if (_isNetworkError) {
				const cellDates = { departDate: new Date(cell.dataset.departdate) };

				if (cell.dataset.returndate) {
					cellDates.returnDate = new Date(cell.dataset.returndate);
				}

				cell.outerHTML = this.renderCell(cellDates);

				// Only show an error message when the final queued cell has been processed
				if (this.searchCount === 0) {
					alert(l('error.e14'));
				}
			} else {
				if (!error?.error_code && error?.message !== 'Price calendar cell: no result!') {
					Civet.reportError(error);
				}

				cell.outerHTML = this.renderCellUnavaliable(cell.dataset.departdate, cell.dataset.returndate);
			}
		}
	}

	queueSearch (cell) {
		const timeoutSearch = async () => {
			if (this.searchCount === 3) {
				if (!cell.classList.contains('priceCalendar__cell--queued')) {
					this.renderCellQueued(cell);
				}

				await sleep(200);
				timeoutSearch();
			} else {
				this.searchCount++;
				this.renderCellSearching(cell);
				this.search(cell);
			}
		};

		timeoutSearch();
	}

	mergeMeta (meta) {
		if (!meta) {
			return;
		}

		let uniqueTimesMerged = false;

		for (const key in meta) {
			if (key === 'data') {
				continue;
			}

			// If empty array or empty object set the initial state
			if ((Array.isArray(this.meta?.[key]) && this.meta?.[key].length === 0) || isEmptyObject(this.meta?.[key] || {})) {
				this.meta[key] = deepCopy(meta[key]);
				continue;
			}

			// If key does not exist yet, set initial state
			if (this.meta?.[key] === undefined) {
				this.meta[key] = deepCopy(meta[key]);
				continue;
			}

			// If plain object
			if (meta[key].constructor === Object) {
				this.meta[key] = Object.assign({}, this.meta[key], meta[key]);
				continue;
			}

			// All the logic below is for forceNearbyDates. We don't merge for normal results as it causes weird behaviour.
			if (!js_params?.urlVars?.forceNearbyDates) {
				continue;
			}

			const defaultSort = (a, b) => a - b;

			// If primitive array
			if (Array.isArray(meta[key]) && meta[key].length > 0 && typeof meta[key][0] !== 'object') {
				this.meta[key] = arrayMergePrimitive(this.meta[key], meta[key]).sort(defaultSort);
				continue;
			}

			// If we merge for none faulty results we get weird behaviour, only merge these filters for this edge case.
			if (['uniqueTimes', 'uniqueTimesOffset'].includes(key)) {
				// Check if we have already merged the uniqueTimes
				const isAlreadyMerged = this.meta[key].every((item, index) => {
					const { departures: metaDep = [], arrivals: metaArr = [] } = meta[key][index] || {};
					const { departures: itemDep = [], arrivals: itemArr = [] } = item || {};
					return metaDep.every(d => itemDep.includes(d)) && metaArr.every(a => itemArr.includes(a));
				});

				if (key === 'uniqueTimes' && isAlreadyMerged) {
					uniqueTimesMerged = true;
					continue;
				}

				if (key === 'uniqueTimesOffset' && uniqueTimesMerged) {
					continue;
				}

				this.meta[key] = this.meta[key].map((item, index) => {
					return {
						departures: [...item.departures, ...meta[key][index].departures].sort(defaultSort),
						arrivals: [...item.arrivals, ...meta[key][index].arrivals].sort(defaultSort)
					};
				});

				continue;
			}

			// If the key is an array of objects merge by a specific key
			if (['uniqueAirportStops', 'uniqueAirportDepartures', 'uniqueAirportArrivals'].includes(key)) {
				const mergeArrayOfObject = (arr1, arr2, mergeKey) => {
					const map = new Map();
					arr1.forEach(item => map.set(item[mergeKey], item));
					arr2.forEach(item => map.set(item[mergeKey], item));
					return Array.from(map.values());
				};

				this.meta[key] = mergeArrayOfObject(this.meta[key], meta[key], 'iata');
				continue;
			}
		}
	}

	blurCells () {
		const cells = qsa('.priceCalendar__cell', this.html);
		const styles = [
			'priceCalendar__cell--highlighted',
			'priceCalendar__cell--headHighlighted',
			'priceCalendar__cell--sideHighlighted',
			'priceCalendar__cell--unavailableHighlighted',
			'priceCalendar__cell--selected',
			'priceCalendar__cell--focused'
		];

		cells.forEach(cell => cell.classList.remove(...styles));
	}

	focusCell (cell) {
		this.highlightCells(cell);
		cell.classList.add('priceCalendar__cell--focused');
	}

	flashCell (cell) {
		cell.classList.add('priceCalendar__cell--flashed');
	}

	getDefaultCell () {
		const departDate = this.departDate;

		let selector = '.priceCalendar__cell';

		if (this.returnTrip) {
			const returnDate = this.returnDate;
			selector += `[data-departdate="${dateFormatYMD(departDate)}"][data-returndate="${dateFormatYMD(returnDate)}"]`;
		} else {
			selector += `[data-departdate="${dateFormatYMD(departDate)}"]`;
		}

		return this.html.querySelector(selector);
	}

	highlightDefaultCells () {
		const cell = this.getDefaultCell();

		if (cell) {
			this.focusCell(cell);
		}
	}

	highlightCells (cell) {
		const col = cell.parentElement;
		const index = getNodeIndex(cell);
		const colCells = prevAll(cell);
		const rowCells = prevAll(col);

		this.blurCells();

		colCells.forEach((c) => {
			const isHead = c.classList.contains('priceCalendar__cell--head');

			if (isHead) {
				c.classList.add('priceCalendar__cell--headHighlighted');
			} else {
				const isUnavailable = c.classList.contains('priceCalendar__cell--unavailable');

				if (isUnavailable) {
					c.classList.add('priceCalendar__cell--unavailableHighlighted');
				} else {
					c.classList.add('priceCalendar__cell--highlighted');
				}
			}
		});

		rowCells.forEach((c) => {
			const d = qsa('.priceCalendar__cell', c)[index];

			if (d) {
				const isSide = d.classList.contains('priceCalendar__cell--side');

				if (isSide) {
					d.classList.add('priceCalendar__cell--sideHighlighted');
				} else {
					const isUnavailable = d.classList.contains('priceCalendar__cell--unavailable');

					if (isUnavailable) {
						d.classList.add('priceCalendar__cell--unavailableHighlighted');
					} else {
						d.classList.add('priceCalendar__cell--highlighted');
					}
				}
			}
		});
	}

	scrollToSelectedDates () {
		const focusedCell = qs('.priceCalendar__cell--focused', this.html);

		if (focusedCell) {
			focusedCell.scrollIntoView({ behaviour: 'smooth', block: 'center', inline: 'center' });
		} else {
			const isReturnTrip = this.returnTrip;
			const grid = this.html.querySelector('.priceCalendar__inner');
			const cell = this.html.querySelector('.priceCalendar__cell');
			const cellWidth = cell.offsetWidth;
			const cellHeight = cell.offsetHeight;

			let xOffset = 0;
			const yOffset = (cellHeight / 2) - cellHeight;

			if (isReturnTrip) {
				xOffset = (cellWidth / 2) - cellWidth;
			}

			grid.scrollLeft = ((grid.scrollWidth - grid.clientWidth) / 2) + xOffset;
			grid.scrollTop = ((grid.scrollHeight - grid.clientHeight) / 2) + yOffset;
		}
	}

	showLoader () {
		this.loader.classList.add('priceCalendar__loader--visible');
	}

	hideLoader () {
		this.loader.classList.remove('priceCalendar__loader--visible');
	}

	renderResultDetails (searchID, sorting) {
		if (searchID === undefined || searchID == '') {
			throw new DOMException('Dataset value id of the priceCalendar cell is either not set or not reachable', 'DatasetUndefined');
		}

		this.activeResult = this.searches[searchID][this.data.sorting];

		// Timetable or timeline might have set resultsRender.response.
		// We only set resultsRender data right before rendering the modal.
		resultsRender.setData(this.meta, resultsRender.searchParams);

		resultsRender
			.renderModal(searchID, this.searches[searchID][this.data.sorting], 0, this.data.filter, {
				onClose () {
					resultsRender.setData(useResultQueryStore().state.response, resultsRender.searchParams);
				}
			}, {}, 'pricecalendar')
			.open();
	}

	initEvents () {
		if (this.initialised) {
			return;
		}

		this.initialised = true;
		const gridWrap = this.html.querySelector('.priceCalendar__inner');

		let scrollFrame;
		let resizeFrame;
		let searchTimer;

		this.events.onClick = (e) => {
			const elem = e.target;

			if (!elem.closest(`#${this.html.id}`)) {
				return;
			}
			// priceCalendar__key--cheap
			if (elem.closest('.priceCalendar__key--cheap')) {
				this.scrollToCheapest();
				return;
			}

			const isHead = elem.classList.contains('priceCalendar__cell--head');
			const isSide = elem.classList.contains('priceCalendar__cell--side');
			const isSearching = elem.closest('.priceCalendar__cell--searching');

			if (isHead || isSide || isSearching) {
				e.stopPropagation();
				return;
			}


			// show result modal with trip data.
			if (elem.matches('.priceCalendar__cell--searched')) {
				const sortingMap = {
					price: 'flightDataCheapest',
					traveltime: 'flightDataFastest',
					bestValue: 'flightDataBest',
					emission: 'flightDataEmission'
				};

				const sorting = sortingMap[this.data.sorting];
				const searchID = elem.dataset.id;

				this.renderResultDetails(searchID, sorting);

				return;
			}

			// A user should not be allowed to queue a cell whilst searchGrid is still loading
			if (elem.matches('.priceCalendar__cell') && !this.searchLoading) {
				this.queueSearch(elem);
			}

			if (elem.closest('.priceCalendar__maximize.active')) {
				logEvent(events.calendarMinimize, eventCategories.feature);
				qs('.priceCalendar__inner').style.removeProperty('max-height');
				elem.closest('.priceCalendar__maximize.active').classList.remove('active');

				return;
			}

			if (elem.closest('.priceCalendar__maximize')) {
				logEvent(events.calendarMaximize, eventCategories.feature);
				qs('.priceCalendar__inner').style.maxHeight = 'calc(85vh)';
				elem.closest('.priceCalendar__maximize').classList.add('active');
				// Search new set of dates
				this.searchGrid();
				// Scroll to top of
				if (isDesktopView()) {
					this.html.scrollIntoView({ behaviour: 'smooth', block: 'start' });
				}
			}

			if (elem.closest('[data-action="center-calendar"]')) {
				this.scrollToSelectedDates();
			}
		};

		this.events.onScroll = (e) => {
			const elem = e.target;

			if (elem !== gridWrap) {
				return;
			}

			window.clearTimeout(searchTimer);

			if (scrollFrame) {
				window.cancelAnimationFrame(scrollFrame);
			}

			scrollFrame = window.requestAnimationFrame(() => {
				this.setVisibleCells();

				if (this.isOpen && !this.searchLoading && this.initialised && this.searchCount === 0) {
					searchTimer = setTimeout(() => this.searchGrid(), 1000);
				}
			});
		};

		let isMobile = isMobileView();

		const scrollIntoViewOnViewPortChange = debounce(() => {
			if (isMobile !== isMobileView()) {
				isMobile = isMobileView();
				this.scrollToSelectedDates();
			}
		}, 100);

		this.events.onResize = () => {
			if (resizeFrame) {
				window.cancelAnimationFrame(resizeFrame);
			}

			resizeFrame = window.requestAnimationFrame(() => {
				scrollIntoViewOnViewPortChange();
				this.setVisibleCells();
			});
		};

		document.addEventListener('click', this.events.onClick);
		this.elems.inner.addEventListener('scroll', this.events.onScroll);
		window.addEventListener('resize', this.events.onResize);
	}

	open () {
		this.isOpen = true;
		this.initEvents();

		const maximizeButton = qs('.priceCalendar__maximize');

		if (maximizeButton && !this.returnTrip) {
			maximizeButton.remove();
		}

		requestAnimationFrame(() => {
			this.scrollToSelectedDates();
			this.setVisibleCells();
			this.searchGrid();
		});
	}

	close () {
		if (this.isOpen) {
			// Abort any ongoing searches
			this.chunkAbortController?.abort?.();

			this.isOpen = false;
			document.removeEventListener('click', this.events.onClick);
			this.elems.inner.removeEventListener('scroll', this.events.onScroll);
			window.removeEventListener('resize', this.events.onResize);

			this.events = {};
			this.initialised = false;
		}
	}
}
